import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Button,
  IconButton,
  Tooltip,
  Badge,
  SimpleGrid,
  useToast,
  Spinner,
  Center
} from '@chakra-ui/react';
import { Search2Icon, AddIcon, CloseIcon } from '@chakra-ui/icons';
import api from '../../services/api';

const ProductLibrary = ({ isOpen, onClose, onProductSelect }) => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  // Load products and categories
  useEffect(() => {
    if (isOpen) {
      loadProducts();
      loadCategories();
    }
  }, [isOpen]);

  // Filter products based on search and category
  useEffect(() => {
    let filtered = products;

    if (selectedCategory) {
      filtered = filtered.filter(product => 
        product.category_id === parseInt(selectedCategory)
      );
    }

    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  }, [products, selectedCategory, searchTerm]);

  const loadProducts = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/v1/products');
      setProducts(response.data.products || []);
    } catch (error) {
      toast({
        title: 'Error loading products',
        description: 'Failed to load product library',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/api/v1/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleProductDragStart = (e, product) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'product',
      product: product
    }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleProductClick = (product) => {
    onProductSelect(product);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
  };

  return (
    <Box
      position="fixed"
      right={isOpen ? 0 : '-400px'}
      top={0}
      bottom={0}
      width="400px"
      bg="white"
      borderLeft="1px"
      borderColor="gray.200"
      boxShadow="lg"
      transition="right 0.3s ease"
      zIndex={1000}
      overflow="hidden"
    >
      {/* Header */}
      <Box
        bg="blue.500"
        color="white"
        p={4}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text fontSize="lg" fontWeight="bold">
          Product Library
        </Text>
        <IconButton
          icon={<CloseIcon />}
          variant="ghost"
          color="white"
          onClick={onClose}
          size="sm"
        />
      </Box>

      {/* Search and Filters */}
      <Box p={4} borderBottom="1px" borderColor="gray.200">
        <VStack spacing={3}>
          <InputGroup>
            <InputLeftElement>
              <Search2Icon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search products..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </InputGroup>
          
          <Select
            placeholder="All Categories"
            value={selectedCategory}
            onChange={handleCategoryChange}
          >
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>

          {(searchTerm || selectedCategory) && (
            <Button
              size="sm"
              variant="ghost"
              onClick={clearFilters}
            >
              Clear Filters
            </Button>
          )}
        </VStack>
      </Box>

      {/* Products List */}
      <Box flex={1} overflow="auto" p={4}>
        {isLoading ? (
          <Center py={8}>
            <Spinner size="lg" />
          </Center>
        ) : filteredProducts.length === 0 ? (
          <Center py={8}>
            <VStack spacing={2}>
              <Text color="gray.500">No products found</Text>
              <Text fontSize="sm" color="gray.400">
                Try adjusting your search or category filter
              </Text>
            </VStack>
          </Center>
        ) : (
          <SimpleGrid columns={2} spacing={4}>
            {filteredProducts.map(product => (
              <Box
                key={product.id}
                border="1px"
                borderColor="gray.200"
                borderRadius="md"
                p={3}
                cursor="pointer"
                _hover={{
                  borderColor: "blue.300",
                  shadow: "md",
                  transform: "translateY(-2px)"
                }}
                transition="all 0.2s"
                draggable
                onDragStart={(e) => handleProductDragStart(e, product)}
                onClick={() => handleProductClick(product)}
              >
                <VStack spacing={2} align="stretch">
                  <Box
                    height="100px"
                    bg="gray.100"
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                  >
                    {product.product_media && product.product_media.length > 0 ? (
                      <Image
                        src={product.product_media[0].url}
                        alt={product.name}
                        maxH="100%"
                        maxW="100%"
                        objectFit="cover"
                      />
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        No Image
                      </Text>
                    )}
                  </Box>
                  
                  <VStack spacing={1} align="start">
                    <Text fontSize="sm" fontWeight="medium" noOfLines={2}>
                      {product.name}
                    </Text>
                    <Text fontSize="xs" color="gray.600" noOfLines={2}>
                      {product.description}
                    </Text>
                    {product.category && (
                      <Badge size="sm" colorScheme="blue">
                        {product.category.name}
                      </Badge>
                    )}
                  </VStack>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Box>

      {/* Footer */}
      <Box p={4} borderTop="1px" borderColor="gray.200">
        <Text fontSize="sm" color="gray.600" textAlign="center">
          Drag and drop products onto the canvas to add them to your floor plan
        </Text>
      </Box>
    </Box>
  );
};

export default ProductLibrary; 